import type { CreatorProfile } from '@/mocks/profiles';

interface ProfileHeaderProps {
  profile: CreatorProfile;
  onSupportClick: () => void;
}

const ProfileHeader = ({ profile, onSupportClick }: ProfileHeaderProps) => {
  const formatAmount = (val: number) =>
    new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(val);

  return (
    <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden mb-6">
      {/* Cover Image */}
      <div className="relative h-48 md:h-64 overflow-hidden">
        <img
          src={profile.coverImage}
          alt="غلاف الملف الشخصي"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
      </div>

      {/* Profile Info */}
      <div className="px-6 pb-6">
        {/* Avatar + Actions Row */}
        <div className="flex items-end justify-between -mt-12 mb-4">
          <div className="relative">
            <img
              src={profile.avatar}
              alt={profile.displayName}
              className="w-24 h-24 rounded-2xl object-cover object-top border-4 border-white shadow-sm"
            />
            {profile.isVerified && (
              <div className="absolute -bottom-1 -left-1 w-7 h-7 flex items-center justify-center bg-emerald-500 rounded-full border-2 border-white">
                <i className="ri-check-line text-white text-xs"></i>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 pb-1">
            <button className="flex items-center gap-2 px-4 py-2 rounded-xl bg-stone-100 hover:bg-stone-200 text-stone-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-share-line text-sm"></i>
              <span className="hidden sm:inline">مشاركة</span>
            </button>
            <button
              onClick={onSupportClick}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-heart-line text-sm"></i>
              <span>دعم المبدع</span>
            </button>
          </div>
        </div>

        {/* Name & Verification */}
        <div className="flex items-center gap-2 mb-1">
          <h1 className="text-2xl font-black text-stone-800">{profile.displayName}</h1>
          {profile.isVerified && (
            <div className="flex items-center gap-1 bg-emerald-50 text-emerald-700 text-xs px-2.5 py-1 rounded-full font-medium">
              <i className="ri-verified-badge-line text-xs"></i>
              موثق
            </div>
          )}
        </div>

        {/* Username & Category */}
        <div className="flex items-center gap-3 mb-3">
          <span className="text-stone-400 text-sm">@{profile.username}</span>
          <span className="text-stone-300">•</span>
          <span className="text-emerald-600 text-sm font-medium">{profile.category}</span>
        </div>

        {/* Bio */}
        <p className="text-stone-600 text-sm leading-relaxed mb-4 max-w-2xl">{profile.bio}</p>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-4 mb-5">
          <div className="flex items-center gap-1.5 text-stone-500 text-sm">
            <i className="ri-map-pin-line text-stone-400"></i>
            <span>{profile.location}</span>
          </div>
          <div className="flex items-center gap-1.5 text-stone-500 text-sm">
            <i className="ri-calendar-line text-stone-400"></i>
            <span>انضم في {profile.joinedDate}</span>
          </div>
          {profile.socialLinks.website && (
            <div className="flex items-center gap-1.5 text-emerald-600 text-sm">
              <i className="ri-global-line text-stone-400"></i>
              <a href={`https://${profile.socialLinks.website}`} target="_blank" rel="nofollow noopener noreferrer" className="hover:underline cursor-pointer">
                {profile.socialLinks.website}
              </a>
            </div>
          )}
        </div>

        {/* Social Links */}
        <div className="flex items-center gap-2 mb-5">
          {profile.socialLinks.twitter && (
            <a href={`https://twitter.com/${profile.socialLinks.twitter}`} target="_blank" rel="nofollow noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-stone-100 hover:bg-stone-800 hover:text-white text-stone-600 transition-colors cursor-pointer">
              <i className="ri-twitter-x-line text-sm"></i>
            </a>
          )}
          {profile.socialLinks.instagram && (
            <a href={`https://instagram.com/${profile.socialLinks.instagram}`} target="_blank" rel="nofollow noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-stone-100 hover:bg-pink-500 hover:text-white text-stone-600 transition-colors cursor-pointer">
              <i className="ri-instagram-line text-sm"></i>
            </a>
          )}
          {profile.socialLinks.youtube && (
            <a href={`https://youtube.com/@${profile.socialLinks.youtube}`} target="_blank" rel="nofollow noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-stone-100 hover:bg-red-500 hover:text-white text-stone-600 transition-colors cursor-pointer">
              <i className="ri-youtube-line text-sm"></i>
            </a>
          )}
          {profile.socialLinks.snapchat && (
            <a href={`https://snapchat.com/add/${profile.socialLinks.snapchat}`} target="_blank" rel="nofollow noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-stone-100 hover:bg-yellow-400 hover:text-white text-stone-600 transition-colors cursor-pointer">
              <i className="ri-snapchat-line text-sm"></i>
            </a>
          )}
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mb-5">
          {profile.badges.map((badge) => (
            <span key={badge} className="flex items-center gap-1.5 bg-amber-50 text-amber-700 border border-amber-200 text-xs px-3 py-1.5 rounded-full font-medium">
              <i className="ri-award-line text-xs"></i>
              {badge}
            </span>
          ))}
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-5 border-t border-stone-100">
          <div className="text-center">
            <div className="text-2xl font-black text-stone-800">{formatAmount(profile.stats.totalRaised)}</div>
            <div className="text-xs text-stone-400 mt-0.5">ريال جُمع</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-black text-stone-800">{formatAmount(profile.stats.totalDonors)}</div>
            <div className="text-xs text-stone-400 mt-0.5">داعم كريم</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-black text-stone-800">{profile.stats.campaignsCount}</div>
            <div className="text-xs text-stone-400 mt-0.5">حملة أطلقها</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-black text-emerald-600">{profile.stats.successRate}%</div>
            <div className="text-xs text-stone-400 mt-0.5">نسبة النجاح</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
